// src/contexts/auth/hooks/registration.ts
/** @jsxImportSource @emotion/react */

import React from "react";
import { useCallback, useEffect, useRef } from "react";
import { unstable_batchedUpdates } from "react-dom";

import type { Profile, RegistrationData, User } from "../types";
import { ACCESS_TOKEN_KEY } from "../helpers";
import { saveProfile } from "../../../helpers/save-profile";
import { loadProfile, loadProfileFromBootData } from "../../../helpers/load-profile";
import { makeAuthLogger } from "./storage";

type MergeResult = {
    merged: RegistrationData;
    changed: boolean;
};

const smartMergeWithChange = (
    base: RegistrationData,
    patch: Partial<RegistrationData>,
): MergeResult => {
    let changed = false;
    const result: RegistrationData = { ...(base ?? {}) };

    for (const [k, v] of Object.entries(patch ?? {})) {
        if (v !== undefined) {
            const prevV = (result as any)[k];
            if (!Object.is(prevV, v)) changed = true;
            // @ts-expect-error index assignment
            result[k] = v;
        }
    }

    return { merged: result, changed };
};

export const useAuthOnboarding = (deps: {
    onboarding: RegistrationData;
    currentStepState: number;
    userId: string | undefined;

    setRegistrationState: React.Dispatch<
        React.SetStateAction<RegistrationData>
    >;
    setCurrentStepState: (v: number) => void;
    setUserId: (v: string | undefined) => void;

    setProfileLoaded: (v: boolean) => void;
    setProfileSaving: (v: boolean) => void;

    // ✅ Option A: separate "refreshing" flag for silent re-sync
    setProfileRefreshing: (v: boolean) => void;

    // ✅ NEW
    setProfile: (v: Profile | null) => void;

    // ✅ NEW
    setUser: (v: User | null) => void;
}) => {
    const {
        onboarding,
        currentStepState,
        userId,
        setRegistrationState,
        setCurrentStepState,
        setUserId,
        setProfileLoaded,
        setProfileSaving,
        setProfileRefreshing,
        setProfile,
        setUser,
    } = deps;

    const L = makeAuthLogger("onboarding");

    // ✅ Always have the latest step available inside async callbacks (no stale closures)
    const currentStepRef = useRef<number>(currentStepState);
    useEffect(() => {
        currentStepRef.current = currentStepState;
    }, [currentStepState]);

    /**
     * ✅ Allows ONE intentional step downgrade (e.g. user pressed "Go Back")
     * Why this exists:
     * - We want to block random downgrades caused by late profile hydration / sync
     * - But we MUST allow intentional back navigation inside the onboarding UI
     */
    const allowStepDowngradeOnceRef = useRef<boolean>(false);

    // ✅ Option A: "boot complete" sentinel
    const hasBootedOnceRef = useRef<boolean>(false);

    // ✅ DEV snapshot on each render (gated)
    L.log("[render] snapshot", {
        userId,
        currentStepState,
        onboardingKeys: Object.keys(onboarding ?? {}),
        onboarding,
    });

    const smartMerge = useCallback(
        (base: RegistrationData, patch: Partial<RegistrationData>) => {
            L.log("[smartMerge] start", {
                baseKeys: Object.keys(base ?? {}),
                patchKeys: Object.keys(patch ?? {}),
                patch,
            });

            const { merged } = smartMergeWithChange(base, patch);

            L.log("[smartMerge] resultKeys", Object.keys(merged ?? {}));
            L.log("[smartMerge] result snapshot", merged);
            return merged;
        },
        [L],
    );

    const setRegistration = useCallback(
        (patch: Partial<RegistrationData>) => {
            L.log("[setRegistration] patch:", patch);

            setRegistrationState((prev) => {
                L.log("[setRegistration] prev snapshot", prev);

                const { merged, changed } = smartMergeWithChange(prev, patch);

                // Keep existing logging shape
                L.log("[setRegistration] merged snapshot", merged);

                /**
                 * Step progression logic:
                 * - On initial boot: Always accept step from DB (even if it seems like "downgrade")
                 * - During active session: Only allow forward progression
                 * - Exception: Allow one intentional backward navigation (back button)
                 */
                let stepChanged = false;
                const mergedStep = (merged as any).registration_step;
                if (typeof mergedStep === "number") {
                    const latest = currentStepRef.current;

                    const isInitialBoot = latest === 0 &&
                        !hasBootedOnceRef.current;

                    if (mergedStep > latest || isInitialBoot) {
                        L.log("[setRegistration] update currentStepState:", {
                            from: latest,
                            to: mergedStep,
                            isInitialBoot,
                        });
                        currentStepRef.current = mergedStep;
                        setCurrentStepState(mergedStep);
                        stepChanged = true;
                    } else if (mergedStep < latest) {
                        if (allowStepDowngradeOnceRef.current) {
                            L.log(
                                "[setRegistration] ALLOW step downgrade (one-time):",
                                {
                                    from: latest,
                                    to: mergedStep,
                                },
                            );
                            allowStepDowngradeOnceRef.current = false; // consume allowance
                            currentStepRef.current = mergedStep;
                            setCurrentStepState(mergedStep);
                            stepChanged = true;
                        } else {
                            L.log("[setRegistration] IGNORE step downgrade:", {
                                mergedStep,
                                latest,
                            });
                        }
                    } else {
                        L.log("[setRegistration] step unchanged:", {
                            step: mergedStep,
                        });
                    }
                }

                // ✅ PERF: if nothing actually changed (and we didn't advance step), return prev to avoid rerender
                if (!changed && !stepChanged) return prev;

                return merged;
            });
        },
        [L, setRegistrationState, setCurrentStepState],
    );

    const setCurrentStep = useCallback(
        (step: number) => {
            L.log("[setCurrentStep] step:", step);

            /**
             * ✅ If UI is intentionally navigating backwards,
             * allow a single downgrade through setRegistration merge logic.
             */
            const latest = currentStepRef.current;
            if (step < latest) {
                L.log(
                    "[setCurrentStep] enabling one-time downgrade allowance:",
                    {
                        from: latest,
                        to: step,
                    },
                );
                allowStepDowngradeOnceRef.current = true;
            }

            setCurrentStepState(step);
        },
        [L, setCurrentStepState],
    );

    /**
     * ✅ Option A:
     * - profileLoaded == "initial boot complete"
     * - refreshes should NOT flip profileLoaded back to false
     * - use profileRefreshing for silent re-sync
     */
    const syncSession = useCallback(
        async (bootData?: { profile: Profile }): Promise<
            { id: string; data: RegistrationData; profile: Profile } | null
        > => {
            L.log("[syncSession] start", { hasBootData: !!bootData });

            const token = localStorage.getItem(ACCESS_TOKEN_KEY);
            L.log("[syncSession] access_token present:", !!token);

            // 1) If no auth token, we still consider "loaded" (no profile to load).
            if (!token) {
                L.log(
                    "[syncSession] no token -> setProfile(null) + setProfileLoaded(true) and return",
                );
                unstable_batchedUpdates(() => {
                    setProfile(null);
                    setProfileRefreshing(false);
                    setProfileLoaded(true);
                });
                hasBootedOnceRef.current = true;
                return null;
            }

            // 2) Token present:
            //    - block ONLY on first boot
            //    - later calls are "refreshing" only (no AppBoot splash)
            if (!hasBootedOnceRef.current) {
                L.log("[syncSession] first boot -> setProfileLoaded(false)");
                setProfileLoaded(false);
            } else {
                L.log("[syncSession] refresh -> setProfileRefreshing(true)");
                setProfileRefreshing(true);
            }

            try {
                let result;
                if (bootData?.profile) {
                    L.log("[syncSession] using pre-fetched boot profile");
                    result = loadProfileFromBootData(bootData.profile);
                } else {
                    L.log("[syncSession] token present -> loading profile via loadProfile()");
                    result = await loadProfile();
                }
                L.log("[syncSession] profile result:", result);

                if (result) {
                    // ✅ prevent late hydration from downgrading the current step
                    const profilePatch: Partial<RegistrationData> = {
                        ...(result.data ?? {}),
                    };

                    const latest = currentStepRef.current;

                    L.log("[syncSession] profilePatch BEFORE downgrade check", {
                        profilePatch,
                        profileStep: (profilePatch as any)?.registration_step,
                        currentStepState: latest,
                    });

                    if (
                        typeof (profilePatch as any).registration_step ===
                            "number" &&
                        (profilePatch as any).registration_step < latest
                    ) {
                        L.log(
                            "[syncSession] skip downgrade from profile registration_step:",
                            {
                                profile:
                                    (profilePatch as any).registration_step,
                                current: latest,
                            },
                        );
                        delete (profilePatch as any).registration_step;
                    }

                    L.log(
                        "[syncSession] profilePatch AFTER downgrade check",
                        profilePatch,
                    );

                    // ✅ PERF: batch the “apply profile” state changes into fewer commits
                    unstable_batchedUpdates(() => {
                        L.log(
                            "[syncSession] setUserId from loadProfile:",
                            result.id,
                        );
                        setUserId(result.id);

                        // ✅ deterministic: loadProfile returns the raw row
                        L.log(
                            "[syncSession] setProfile(result.profile) keys:",
                            {
                                keys: Object.keys(
                                    (result as any)?.profile ?? {},
                                ),
                            },
                        );
                        setProfile((result as any).profile as Profile);

                        L.log(
                            "[syncSession] setRegistration from loadProfile:",
                            profilePatch,
                        );
                        setRegistration(profilePatch);
                    });

                    L.log("[syncSession] done applying profile");
                    return result;
                } else {
                    L.log(
                        "[syncSession] loadProfile() returned null/undefined",
                    );
                    return null;
                }
            } catch (e) {
                L.error("[syncSession] loadProfile failed", e);
                return null;
            } finally {
                L.log(
                    "[syncSession] finally -> setProfileLoaded(true) + setProfileRefreshing(false)",
                );
                unstable_batchedUpdates(() => {
                    setProfileLoaded(true);
                    setProfileRefreshing(false);
                });
                hasBootedOnceRef.current = true;
            }
        },
        [
            L,
            setRegistration,
            setProfile,
            setProfileLoaded,
            setProfileRefreshing,
            setUserId,
        ],
    );

    const persistRegistrationIfNeeded = useCallback(async () => {
        const step = (onboarding as any)?.registration_step;

        // ✅ Skip if registration is already complete — profile was just loaded from
        // the server (step 0→10 during boot hydration), no need to write it back.
        const isCompleted =
            !!(onboarding as any)?.registration_completed &&
            !!(onboarding as any)?.onboarding_completed;
        if (isCompleted) return;

        // ✅ only require a token (since /profiles/me is auth-bound)
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (!token) return;

        // ✅ require a real step number (you can allow 0)
        if (typeof step !== "number") return;

        setProfileSaving(true);
        try {
            const safe: any = { ...(onboarding as any) };
            const hadPassword = !!safe.password;
            delete safe.password;

            const updatedProfileRow = await saveProfile(safe);

            // ✅ now works because saveProfile returns a row
            if ((updatedProfileRow as any)?.id) {
                unstable_batchedUpdates(() => {
                    setProfile(updatedProfileRow as any);
                    // also keep userId in sync if you want
                    setUserId?.((updatedProfileRow as any).id);
                });
            }

            // (optional) clear password after save ONLY if it was previously sent and saved
            if (hadPassword) {
                setRegistration({ password: undefined } as any);
            }
        } catch (e) {
            L.warn("[persistRegistrationIfNeeded] saveProfile failed", e);
        } finally {
            setProfileSaving(false);
        }
    }, [
        L,
        onboarding,
        setRegistration,
        setProfile,
        setProfileSaving,
        setUser,
        userId,
    ]);

    return {
        smartMerge,
        setRegistration,
        setCurrentStep,
        syncSession,
        persistRegistrationIfNeeded,
    };
};
